import React, { useEffect, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { School, Hospital, Utensils, ShoppingBag, Train, MapPin, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';

const LIBRARIES = ['places', 'geometry'];

const AMENITY_TYPES = [
    { key: 'school', icon: School, label: 'Schools', type: 'school' },
    { key: 'hospital', icon: Hospital, label: 'Hospitals', type: 'hospital' },
    { key: 'restaurant', icon: Utensils, label: 'Restaurants', type: 'restaurant' },
    { key: 'mall', icon: ShoppingBag, label: 'Shopping', type: 'shopping_mall' },
    { key: 'transit', icon: Train, label: 'Transit', type: 'transit_station' }
];

export default function NeighborhoodInsights({ lat, lng }) {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES
    });

    const [insights, setInsights] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isLoaded && lat && lng) {
            fetchNearbyAmenities();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, lat, lng]);

    const fetchNearbyAmenities = () => {
        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        const location = new window.google.maps.LatLng(lat, lng);

        const fetchType = (amenity) => {
            return new Promise((resolve) => {
                service.nearbySearch(
                    { location, radius: 3000, type: amenity.type },
                    (results, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                            resolve({ key: amenity.key, data: results.slice(0, 3) });
                        } else {
                            resolve({ key: amenity.key, data: [] });
                        }
                    }
                );
            });
        };

        Promise.all(AMENITY_TYPES.map(fetchType)).then((results) => {
            const newInsights = {};
            results.forEach((r) => {
                newInsights[r.key] = r.data;
            });
            setInsights(newInsights);
            setLoading(false);
        });
    };

    if (!isLoaded || loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-32 bg-navy-50/50 rounded-xl animate-pulse border border-navy-100/50" />
                ))}
            </div>
        );
    }

    const hasAnyInsights = Object.values(insights).some(arr => arr.length > 0);

    if (!hasAnyInsights) {
        return <p className="text-navy-500 italic">No major amenities found within 3km.</p>;
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AMENITY_TYPES.map((amenity) => {
                const Icon = amenity.icon;
                const places = insights[amenity.key] || [];
                if (places.length === 0) return null;

                return (
                    <div key={amenity.key} className="p-4 rounded-xl border border-navy-100 bg-white hover:shadow-md transition-shadow space-y-3">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-2 bg-sky-50 rounded-lg">
                                <Icon className="w-4 h-4 text-sky-600" />
                            </div>
                            <h3 className="font-bold text-navy-900">{amenity.label}</h3>
                        </div>
                        <div className="space-y-2">
                            {places.map((place) => (
                                <div key={place.place_id} className="flex flex-col">
                                    <span className="text-sm font-medium text-navy-800 line-clamp-1">{place.name}</span>
                                    <span className="text-[10px] text-navy-400 flex items-center gap-1">
                                        <MapPin className="w-2.5 h-2.5" />
                                        {(window.google.maps.geometry.spherical.computeDistanceBetween(
                                            new window.google.maps.LatLng(lat, lng),
                                            place.geometry.location
                                        ) / 1000).toFixed(1)} km away
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
